import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { CustomDialog } from '../../../Component/Dialog';
import ComponentRegistry from '../../../ProjectCanvas/ComponentRegistry';
import { send_request } from '../../../../utils/Request';
import {
  LEGACY_FORM_TYPES,
  DYNAMIC_FORM_TYPES,
  isDynamicForm,
} from '../../../../utils/formTypeConstants';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';

// Determine the conversion-target list for a given source component type. Legacy and v2 form
// families are isolated from each other — the conversion services only know how to map within a
// family — so we pick the family from the source and exclude the source itself.
const getDefaultTargetsForSource = (sourceType) => {
  if (DYNAMIC_FORM_TYPES.includes(sourceType)) return DYNAMIC_FORM_TYPES;
  return LEGACY_FORM_TYPES;
};

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '8px',
}));

const SourceCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
}));

const SourceLogo = styled('img')({
  width: 22,
  height: 22,
  flexShrink: 0,
});

const SourceName = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const ArrowWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '10px 0',
  color: theme.palette.text.secondary,
}));

const TileGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 8,
});

const Tile = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  padding: '12px 14px',
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: selected
    ? theme.palette.mode === 'dark'
      ? 'rgba(38, 112, 255, 0.12)'
      : 'rgba(38, 112, 255, 0.06)'
    : theme.palette.background.paper,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  outline: 'none',
  '&:hover': {
    borderColor: selected
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
    backgroundColor: selected
      ? theme.palette.mode === 'dark'
        ? 'rgba(38, 112, 255, 0.16)'
        : 'rgba(38, 112, 255, 0.08)'
      : theme.palette.action.hover,
  },
  '&:focus-visible': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
  },
}));

const TileHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
});

const TileLogo = styled('img')({
  width: 18,
  height: 18,
  flexShrink: 0,
});

const TileTitle = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
  lineHeight: 1.3,
}));

const TileDescription = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const SelectedDot = styled(CheckCircleIcon)(({ theme }) => ({
  width: 16,
  height: 16,
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const Banner = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ theme, tone }) => {
  const palette =
    tone === 'warning'
      ? {
          bg:
            theme.palette.mode === 'dark'
              ? 'rgba(237, 108, 2, 0.12)'
              : 'rgba(237, 108, 2, 0.08)',
          border: 'rgba(237, 108, 2, 0.4)',
          fg: theme.palette.warning.main,
        }
      : {
          bg:
            theme.palette.mode === 'dark'
              ? 'rgba(38, 112, 255, 0.12)'
              : 'rgba(38, 112, 255, 0.06)',
          border: 'rgba(38, 112, 255, 0.35)',
          fg: theme.palette.info?.main || theme.palette.primary.main,
        };
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 12,
    padding: '8px 10px',
    border: `1px solid ${palette.border}`,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: palette.bg,
    color: palette.fg,
    '& .banner-icon': {
      marginTop: '1px',
      flexShrink: 0,
    },
    '& .banner-text': {
      fontSize: NODE_UI.fontSize.caption,
      lineHeight: 1.45,
      color: theme.palette.text.primary,
    },
  };
});

const ConvertFormDialog = ({
  component,
  open,
  handleClose,
  projectId,
  componentId,
  formTargets,
  handleComponentUpdated,
}) => {
  const [changeTo, setChangeTo] = useState(null);
  const [loading, setLoading] = useState(false);

  const allowedTargets =
    formTargets && formTargets.length > 0
      ? formTargets
      : getDefaultTargetsForSource(component.type);

  const targetOptions = useMemo(
    () =>
      ComponentRegistry.filter(
        (comp) =>
          allowedTargets.indexOf(comp.name) !== -1 && comp.name !== component.type
      ),
    [allowedTargets, component.type]
  );

  const handleCancel = () => {
    handleClose();
    setChangeTo(null);
  };

  const handleConvert = async () => {
    if (!changeTo) return;
    setLoading(true);
    const body = {
      projectId,
      type: changeTo.name,
      componentId,
    };

    try {
      const res = await send_request(
        `component-service/convert/${component.type}/into/${body.type}`,
        body,
        '',
        'POST'
      );
      handleComponentUpdated(res.data, changeTo);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
      setChangeTo(null);
      handleClose();
    }
  };

  // Warn the user about data loss / extra setup that is specific to a given source/target pair.
  const banner = useMemo(() => {
    if (!changeTo) return null;
    const sourceType = component.type;
    const targetType = changeTo.name;

    if (
      sourceType === 'sequential_form' &&
      (targetType === 'form' || targetType === 'form_section')
    ) {
      return {
        tone: 'warning',
        text:
          'Converting an Assign Task to a Form or Form Section will discard all task settings (assigned users, task subject, task description, reminder email etc).',
      };
    }

    if (
      sourceType === 'dynamic_review_task' &&
      (targetType === 'dynamic_form' || targetType === 'form_step')
    ) {
      return {
        tone: 'warning',
        text:
          'Task settings (assignees, completion rule, notifications) will be discarded. Form fields, title, and description are preserved.',
      };
    }

    if (isDynamicForm(sourceType) && targetType === 'dynamic_review_task') {
      return {
        tone: 'info',
        text:
          'You will need to configure assignees and notifications on the Create Task after converting.',
      };
    }

    return null;
  }, [changeTo, component.type]);

  return (
    <CustomDialog
      isOpen={open}
      handleClose={handleCancel}
      size="sm"
      title="Convert component"
      contents={
        <Box sx={{ pt: 0.5 }}>
          <SectionLabel>Currently</SectionLabel>
          <SourceCard>
            {component.logo && <SourceLogo src={component.logo} alt="" />}
            <SourceName>{component.displayName}</SourceName>
          </SourceCard>

          <ArrowWrap>
            <ArrowDownwardIcon sx={{ fontSize: 18 }} />
          </ArrowWrap>

          <SectionLabel>Convert to</SectionLabel>
          <TileGrid>
            {targetOptions.map((opt) => {
              const selected = changeTo?.name === opt.name;
              return (
                <Tile
                  key={opt.name}
                  type="button"
                  selected={selected}
                  onClick={() => setChangeTo(opt)}
                  disabled={loading}
                >
                  <TileHeader>
                    {opt.logo && <TileLogo src={opt.logo} alt="" />}
                    <TileTitle>{opt.displayName}</TileTitle>
                    {selected && <SelectedDot />}
                  </TileHeader>
                  {opt.description && (
                    <TileDescription>{opt.description}</TileDescription>
                  )}
                </Tile>
              );
            })}
          </TileGrid>

          {banner && (
            <Banner tone={banner.tone}>
              {banner.tone === 'warning' ? (
                <WarningAmberIcon className="banner-icon" sx={{ fontSize: 16 }} />
              ) : (
                <InfoOutlinedIcon className="banner-icon" sx={{ fontSize: 16 }} />
              )}
              <span className="banner-text">{banner.text}</span>
            </Banner>
          )}
        </Box>
      }
      buttons={
        <>
          <Button variant="outlined" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            disabled={loading || !changeTo}
            variant="contained"
            color="primary"
            onClick={handleConvert}
          >
            {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
            Convert
          </Button>
        </>
      }
    />
  );
};

ConvertFormDialog.propTypes = {
  component: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func,
  handleComponentUpdated: PropTypes.func,
  componentId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  formTargets: PropTypes.arrayOf(PropTypes.string),
};

export default ConvertFormDialog;
